import React, { useState, useEffect } from 'react'
import { getCommunity } from '../services/onboardingService'

/**
 * TrialBanner — shows trial status and expiry countdown at the top of the
 * portal when the community is on a TRIAL plan.
 *
 * Props:
 *   communityId  {string}        — the current community ID
 *   onUpgrade    {async ()=>void} — (optional) called when user clicks "Upgrade"; if async, button shows loading state
 */
export default function TrialBanner({ communityId, onUpgrade }) {
  const [community, setCommunity] = useState(null)
  const [daysLeft, setDaysLeft] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const [upgradeLoading, setUpgradeLoading] = useState(false)
  const [upgradeSubmitted, setUpgradeSubmitted] = useState(false)
  const [upgradeError, setUpgradeError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const refreshCommunity = async () => {
    if (!communityId) return
    setRefreshing(true)
    try {
      const data = await getCommunity({ communityId })
      setCommunity(data)
      if (data?.TRIAL_END) {
        const end = new Date(data.TRIAL_END)
        const now = new Date()
        const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24))
        setDaysLeft(diff)
      } else {
        setDaysLeft(null)
      }
    } catch (err) {
      // non-critical refresh failure
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (!communityId) return
    let mounted = true

    const refreshWhenReady = async () => {
      if (!mounted) return
      await refreshCommunity()
    }

    refreshWhenReady()
    window.addEventListener('focus', refreshWhenReady)
    const intervalId = window.setInterval(refreshWhenReady, 15000)
    return () => {
      mounted = false
      window.removeEventListener('focus', refreshWhenReady)
      window.clearInterval(intervalId)
    }
  }, [communityId])

  async function handleUpgrade() {
    if (!onUpgrade) return
    setUpgradeLoading(true)
    setUpgradeError('')
    try {
      await onUpgrade()
      setUpgradeSubmitted(true)
    } catch (err) {
      setUpgradeError(err.message || 'Failed to submit. Please try again.')
    } finally {
      setUpgradeLoading(false)
    }
  }

  if (dismissed || !community) return null
  const plan = (community.PLAN || '').toUpperCase()
  if (plan !== 'TRIAL') return null

  const expired = daysLeft !== null && daysLeft <= 0
  const urgent = daysLeft !== null && daysLeft <= 14

  return (
    <div style={{
      background: expired ? '#fef2f2' : urgent ? '#fff7ed' : '#eff6ff',
      borderBottom: `2px solid ${expired ? '#fca5a5' : urgent ? '#fdba74' : '#93c5fd'}`,
      fontSize: 13,
    }} className="px-4 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 sm:gap-3">

      {/* Left — icon + message */}
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <span style={{ fontSize: 16, flexShrink: 0 }}>{expired ? '⚠️' : urgent ? '⏰' : '🎉'}</span>
        <span style={{ color: expired ? '#991b1b' : urgent ? '#92400e' : '#1e40af' }} className="leading-snug">
          {expired
            ? 'Your free trial has expired. Upgrade to continue using all features.'
            : daysLeft !== null
              ? `Free trial — ${daysLeft} day${daysLeft === 1 ? '' : 's'} remaining`
              : 'Free Trial Active'}
          {' · '}
          <strong>{community.COMMUNITY_NAME}</strong>
        </span>
      </div>

      {/* Right — action + dismiss */}
      <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          {upgradeError && (
            <span style={{ fontSize: 11, color: '#dc2626' }}>{upgradeError}</span>
          )}
          {typeof onUpgrade === 'function' && !upgradeSubmitted && (
            <button
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              style={{
                background: expired ? '#dc2626' : '#1d4ed8',
                color: '#fff', border: 'none', borderRadius: 6,
                padding: '6px 14px', fontSize: 12, fontWeight: 600,
                cursor: upgradeLoading ? 'default' : 'pointer',
                opacity: upgradeLoading ? 0.7 : 1,
              }}
            >
              {upgradeLoading ? 'Submitting…' : 'Request Upgrade'}
            </button>
          )}
          {typeof onUpgrade !== 'function' && (
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: expired ? '#991b1b' : urgent ? '#92400e' : '#1e40af',
            }} className="leading-snug">
              Contact Your Community Administrator For Plan Upgrade
            </span>
          )}
          {upgradeSubmitted && (
            <span style={{ fontSize: 12, color: expired ? '#991b1b' : '#1e40af', fontWeight: 600 }}>
              ✓ Request sent! We'll contact you soon.
            </span>
          )}
          <button
            onClick={refreshCommunity}
            disabled={refreshing}
            style={{
              background: '#e5e7eb',
              color: '#111827',
              border: 'none',
              borderRadius: 6,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.7 : 1,
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        {!expired && !upgradeSubmitted && (
          <button
            onClick={() => setDismissed(true)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}
